/**
 * POST-MATCH EGO STATS — the summary that makes a player feel good about the
 * run they just had (owner: "stats which satisfy players ego and make them
 * feel proud").
 *
 * Design rules, deliberately opinionated:
 *   1. SUPERLATIVE-FIRST — lead with whatever was best about THIS run, and
 *      compare against the player's OWN history, never an absolute bar. "New
 *      top speed" lands for a beginner and a veteran alike.
 *   2. NEVER a bare zero — the picker always returns headlines; a goalless
 *      match still praises distance, speed, or persistence. An empty summary
 *      screen is a punishment, and punishment is the opposite of retention.
 *   3. Records persist — beating "your best" is the hook that carries a player
 *      into the next match. Career totals only ever go up.
 *
 * Signal-light like challenges.ts: the tracker consumes only what the loops
 * already have (controlled player's movement per tick, goal events, the clock).
 * Same injectable store seam as the challenge PBs.
 */
import type { PbStore } from './challenges.js';

export interface MatchRunStats {
  goals: number;
  teamGoals: number;
  /** Metres covered by the controlled player. */
  distanceM: number;
  /** Fastest sustained speed, m/s. */
  topSpeedMs: number;
  /** Seconds spent above the sprint threshold. */
  sprintS: number;
  durationS: number;
}

export interface CareerTotals {
  matches: number;
  goals: number;
  distanceM: number;
  bestGoals: number;
  bestTopSpeedMs: number;
  bestDistanceM: number;
}

const KEY = 'fg.career.v1';
const SPRINT_MS = 5.5; // m/s — matches the sim's sprint band, presentation only

export class MatchStatsTracker {
  private readonly s: MatchRunStats = { goals: 0, teamGoals: 0, distanceM: 0, topSpeedMs: 0, sprintS: 0, durationS: 0 };
  private lastX = NaN;
  private lastZ = NaN;

  /** Feed each sim tick with the CONTROLLED player's position. Handles takeover
   *  switches: a teleport-sized jump re-seeds instead of crediting 40 m. */
  onTick(dtS: number, x: number, z: number): void {
    this.s.durationS += dtS;
    if (Number.isFinite(this.lastX)) {
      const d = Math.hypot(x - this.lastX, z - this.lastZ);
      const v = dtS > 0 ? d / dtS : 0;
      if (v < 13) { // beyond any legal sprint = a switch/reset, not running
        this.s.distanceM += d;
        if (v > this.s.topSpeedMs) this.s.topSpeedMs = v;
        if (v >= SPRINT_MS) this.s.sprintS += dtS;
      }
    }
    this.lastX = x;
    this.lastZ = z;
  }

  onGoal(byMe: boolean, byMyTeam: boolean): void {
    if (byMyTeam) this.s.teamGoals += 1;
    if (byMe) this.s.goals += 1;
  }

  get stats(): Readonly<MatchRunStats> { return this.s; }
}

export function loadCareer(store: PbStore): CareerTotals {
  try {
    const raw = store.get(KEY);
    if (raw) {
      const c = JSON.parse(raw) as CareerTotals;
      if (typeof c.matches === 'number') return c;
    }
  } catch { /* corrupted → fresh career, never a crash */ }
  return { matches: 0, goals: 0, distanceM: 0, bestGoals: 0, bestTopSpeedMs: 0, bestDistanceM: 0 };
}

/** Fold a finished run into the career and persist. Returns the UPDATED career
 *  plus which records this run broke — the headline material. */
export function recordMatch(store: PbStore, run: MatchRunStats): {
  career: CareerTotals;
  broke: { goals: boolean; topSpeed: boolean; distance: boolean };
} {
  const c = loadCareer(store);
  const broke = {
    goals: run.goals > c.bestGoals,
    topSpeed: run.topSpeedMs > c.bestTopSpeedMs,
    distance: run.distanceM > c.bestDistanceM,
  };
  c.matches += 1;
  c.goals += run.goals;
  c.distanceM += run.distanceM;
  if (broke.goals) c.bestGoals = run.goals;
  if (broke.topSpeed) c.bestTopSpeedMs = run.topSpeedMs;
  if (broke.distance) c.bestDistanceM = run.distanceM;
  store.set(KEY, JSON.stringify(c));
  return { career: c, broke };
}

const kmh = (ms: number): string => `${(ms * 3.6).toFixed(1)} km/h`;
const km = (m: number): string => (m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`);

/**
 * The ego layer: 2–3 headlines, best first, NEVER empty. Records outrank raw
 * numbers; raw numbers are framed as achievements, not measurements.
 */
export function pickHeadlines(run: MatchRunStats, career: CareerTotals,
  broke: { goals: boolean; topSpeed: boolean; distance: boolean }): string[] {
  const out: string[] = [];
  if (broke.goals && run.goals > 0) out.push(`★ CAREER BEST — ${run.goals} goal${run.goals > 1 ? 's' : ''} in one match`);
  if (broke.topSpeed && run.topSpeedMs > 6) out.push(`★ NEW TOP SPEED — ${kmh(run.topSpeedMs)}`);
  if (broke.distance && run.distanceM > 400) out.push(`★ MOST GROUND EVER COVERED — ${km(run.distanceM)}`);
  if (run.goals >= 3) out.push(`HAT-TRICK HERO — ${run.goals} goals`);
  else if (run.goals > 0 && out.length < 2) out.push(`ON THE SCORESHEET — ${run.goals} goal${run.goals > 1 ? 's' : ''}`);
  if (out.length < 2 && run.topSpeedMs > 7) out.push(`BLISTERING PACE — hit ${kmh(run.topSpeedMs)}`);
  if (out.length < 2 && run.distanceM > 200) out.push(`ENGINE OF THE TEAM — ${km(run.distanceM)} covered`);
  if (out.length < 2 && run.sprintS > 10) out.push(`RELENTLESS — ${Math.round(run.sprintS)}s at full sprint`);
  // The floor: persistence itself is praised. Career totals only go up, so
  // there is ALWAYS a true, rising number to hand the player.
  if (out.length === 0) out.push(`MATCH ${career.matches} OF YOUR CAREER — ${km(career.distanceM)} covered all-time`);
  return out.slice(0, 3);
}
